import { useState } from 'react';
import { Title } from 'react-admin';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  Divider, Stack, TextField, InputAdornment, IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';
import { tokens } from '../theme.js';

// All help content as data so it can be searched/filtered.
const HELP_SECTIONS = [
  {
    title: 'The basics',
    intro: 'Start here.',
    defaultExpanded: true,
    items: [
      { name: 'Saving', what: 'Every screen has a Save button — changes go live on the website once you save.' },
      { name: 'Publish switch', what: 'Pages, news and gallery albums have a “Published / Visible” toggle. Turn it off to hide something without deleting it.' },
      { name: 'Images', what: 'Click Upload on any image field to pick a file from your computer. It is optimised automatically — no need to resize first.' },
      { name: 'Ordering', what: 'Lists with a “Sort order” number show smaller numbers first. Change the numbers to reorder.' },
      { name: 'Menu sections', what: 'The left sidebar groups everything: Homepage, Editorial, Media, Content, People, Operations, Inbox and Settings.' },
    ],
  },
  {
    title: 'Editing a page (Content → Pages)',
    intro: 'The most-used screen. Every page on the site is one row here — open a page to control all of it from one place.',
    defaultExpanded: true,
    items: [
      { name: 'Title', what: 'Used in menus, the breadcrumb and the browser tab.' },
      { name: 'Headline', what: 'The big heading shown on the page. Wrap words in *asterisks* to make them gold + italic — e.g. “Spaces built for *learning*”. Leave blank to use the Title.' },
      { name: 'Lede', what: 'The short intro paragraph under the heading.' },
      { name: 'Banner / hero image', what: 'The large image across the top of the page. Every page can have one; leave empty for a sensible default.' },
      { name: 'Portrait + two-column', what: 'Optional. Add a Portrait image to switch the page to a two-column layout (portrait + caption on the left, your text on the right) — like the Principal’s Message. Leave empty for a normal full-width page.' },
      { name: 'Gold corner frame', what: 'Optional decorative gold accent on the portrait.' },
      { name: 'Body', what: 'The main content, written in the rich-text editor (see “The Body editor” below).' },
      { name: 'Breadcrumbs', what: 'Set a “Parent group” to show Home / Group / This page at the top. Optional “Parent link” makes the group clickable.' },
      { name: 'Meta description (SEO)', what: 'The summary shown in Google results and link previews. Aim for under 160 characters.' },
      { name: 'Layout chip', what: 'System pages (News, About, etc.) show a read-only layout tag — you control their header, banner and SEO here; their built-in layout comes from the site.' },
    ],
  },
  {
    title: 'The Body editor (rich text)',
    intro: 'The toolbar above the Body field. Select text first, then click a button to apply it.',
    items: [
      { name: 'Headings', what: 'H2 / H3 for section titles.' },
      { name: 'Text styles', what: 'Bold, Italic, Underline, Strikethrough, and Highlight.' },
      { name: 'Text colour', what: 'The “A” colour button — pick a colour for the selected text (or Default to clear it).' },
      { name: 'Alignment', what: 'Left, Centre, Right or Justify for paragraphs and headings.' },
      { name: 'Lists & quote', what: 'Bullet list, numbered list, and block quote.' },
      { name: 'Links', what: 'Select text → link button → paste a URL. Clear the box to remove a link.' },
      { name: 'Images', what: 'Insert an image, then (with it selected) choose S / M / L / Full to resize it.' },
      { name: 'Link an image', what: 'Click an image to select it, then press the link button in the toolbar and paste a URL — visitors can then click the image. Linked images show a gold outline while editing; clear the box to remove the link.' },
      { name: 'Tables', what: 'Table menu → Insert 3×3 table; add/delete rows & columns; drag column edges to resize; “Hide borders” for a clean image/text layout.' },
      { name: 'Content tabs', what: 'The tab button inserts a row of buttons that load another page’s content when clicked — great for splitting a long topic. Each tab needs a label and a page link (e.g. /page/scholarship).' },
      { name: 'Collapsible content', what: 'The up/down-arrows button inserts an accordion (expand/collapse sections). Type a label for each section and write its content directly underneath — text, images, anything. Use “+ Add section” for more, “×” to remove one. Unlike Content tabs, the content lives on this page — no separate page needed.' },
      { name: 'How collapsible sections behave', what: 'On the website the labels sit in a vertical list and every section starts closed. Clicking a label slides its content open with a smooth animation (the + turns into a −); clicking the same label again closes it. Visitors can have several sections open at once.' },
      { name: 'Bigger editor', what: 'Drag the handle at the bottom-right of the writing area to make the editor taller while you work.' },
      { name: 'Undo / Redo', what: 'Step back or forward through your changes.' },
    ],
  },
  {
    title: 'Homepage',
    intro: 'Everything that appears on the home page, top to bottom.',
    items: [
      { name: 'Welcome', what: '“Discover Maple Leaf” intro block (eyebrow, title, text, two buttons).' },
      { name: 'Popup', what: 'The optional modal shown on the homepage — text / image / video. Turn it on or off and choose its type.' },
      { name: 'Slides', what: 'The big rotating hero banner at the very top.' },
      { name: 'Stats', what: 'The numbers strip (e.g. “52 years”, “98% placement”).' },
      { name: 'Advantage', what: 'The “Why Maple Leaf” heading + intro above the feature cards.' },
      { name: 'Why Maple Leaf', what: 'The feature cards (the accordion panels) themselves.' },
      { name: 'Level cards', what: 'The academic-levels cards.' },
      { name: 'Leadership messages', what: 'Messages from leadership shown on the homepage.' },
      { name: 'Partners', what: 'The partner / accreditation logos strip.' },
      { name: 'Campuses', what: 'The campus selector entries.' },
      { name: 'Facilities', what: 'The facilities shown in the campus/facilities areas.' },
    ],
  },
  {
    title: 'Editorial — News, Events, Achievements',
    intro: 'Time-based content with its own listing pages.',
    items: [
      { name: 'News', what: 'Articles and notices. Set a category, featured flag, and Published status. Each gets its own page.' },
      { name: 'Events', what: 'Upcoming and past events with dates and locations.' },
      { name: 'Achievements', what: 'Student/school achievements, grouped by category.' },
    ],
  },
  {
    title: 'Media — Gallery',
    intro: 'Photo galleries shown on the Photos page.',
    items: [
      { name: 'Gallery albums', what: 'Create an album (title + cover). Publish to show it on the site.' },
      { name: 'Gallery images', what: 'The photos inside each album.' },
    ],
  },
  {
    title: 'People — Faculty, Alumni, Addresses',
    intro: '',
    items: [
      { name: 'Faculty', what: 'Staff/teacher profiles.' },
      { name: 'Alumni', what: 'Alumni entries shown on the Alumni page.' },
      { name: 'Addresses', what: 'The school’s campus addresses (used in Contact / footer).' },
    ],
  },
  {
    title: 'Operations — Calendar, Careers',
    intro: '',
    items: [
      { name: 'Calendar', what: 'Academic calendar entries (term dates, holidays, milestones).' },
      { name: 'Careers', what: 'Job openings. Set “Open” to list a role on the Careers page.' },
    ],
  },
  {
    title: 'Inbox — form submissions',
    intro: 'What visitors send you. Read-only, except for marking status.',
    items: [
      { name: 'Admission enquiries', what: 'Enquiry-form submissions. Each one is also emailed to its class’s recipients — the Notification column shows whether that email was sent. Filter by class; mark them new / contacted / closed.' },
      { name: 'Alumni registrations', what: 'Former students who registered via the public alumni form. Search by name, date of birth, class left or student ID; filter by how they left; export to a spreadsheet; mark each as new / verified / archived.' },
      { name: 'Contact submissions', what: 'Contact-form messages; mark as read.' },
      { name: 'Newsletter subscribers', what: 'Email addresses collected from the newsletter sign-up.' },
    ],
  },
  {
    title: 'Settings (super-admins only)',
    intro: 'Site-wide configuration. Visible only to super-admin accounts.',
    items: [
      { name: 'Site settings', what: 'School name, logo, contact details, footer, social links, and homepage band content.' },
      { name: 'Admission classes', what: 'The classes in the Admission page’s “Applying for class” dropdown, and which email address(es) receive each class’s enquiries. Drag to reorder; switch off to hide a class.' },
      { name: 'Menus & links', what: 'The header navigation, dropdowns, topbar logins and footer link lists.' },
      { name: 'Users', what: 'Admin accounts and their roles.' },
      { name: 'Activity logs', what: 'A record of recent admin changes.' },
    ],
  },
];

// Wrap the first case-insensitive match of `q` in a highlight mark.
const Highlight = ({ text, q }) => {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="mark" sx={{ background: tokens.goldPale, px: '1px', borderRadius: '2px' }}>
        {text.slice(idx, idx + q.length)}
      </Box>
      {text.slice(idx + q.length)}
    </>
  );
};

const FeatureList = ({ items, q }) => (
  <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
    {items.map((it) => (
      <Box component="li" key={it.name} sx={{ mb: 1.1, display: 'flex', gap: 1.2, alignItems: 'baseline' }}>
        <Typography component="span" sx={{ fontWeight: 700, color: tokens.green, minWidth: 150, flexShrink: 0 }}>
          <Highlight text={it.name} q={q} />
        </Typography>
        <Typography component="span" variant="body2" sx={{ color: tokens.ink2 }}>
          <Highlight text={it.what} q={q} />
        </Typography>
      </Box>
    ))}
  </Box>
);

export const Help = () => {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  // Filter sections + items against the query.
  const sections = HELP_SECTIONS.map((sec) => {
    const titleMatch = !q || sec.title.toLowerCase().includes(q) || (sec.intro || '').toLowerCase().includes(q);
    const items = (!q || titleMatch)
      ? sec.items
      : sec.items.filter((it) => `${it.name} ${it.what}`.toLowerCase().includes(q));
    return { ...sec, items, show: !q || titleMatch || items.length > 0 };
  }).filter((s) => s.show);

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto', py: 2 }}>
      <Title title="Help & guide" />

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <HelpOutlineIcon sx={{ color: tokens.gold }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: tokens.ink }}>Help &amp; guide</Typography>
      </Stack>
      <Typography variant="body1" sx={{ color: tokens.ink3, mb: 2 }}>
        How to control every part of the Maple Leaf website from this panel. Search or open a section below.
      </Typography>

      <TextField
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search the guide… (e.g. banner, popup, table, colour)"
        fullWidth
        size="small"
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setQuery('')} aria-label="Clear search">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {sections.length === 0 ? (
        <Typography sx={{ color: tokens.ink3, py: 4, textAlign: 'center' }}>
          No matches for “{query}”. Try a different word.
        </Typography>
      ) : (
        sections.map((sec) => (
          <Accordion
            // Re-key on search state so default-expansion re-applies cleanly.
            key={sec.title + (q ? '-q' : '')}
            defaultExpanded={q ? true : !!sec.defaultExpanded}
            disableGutters
            sx={{ '&:before': { display: 'none' }, border: `1px solid ${tokens.line}`, borderRadius: 1, mb: 1, overflow: 'hidden' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ background: tokens.paper }}>
              <Typography sx={{ fontWeight: 700, color: tokens.ink }}>
                <Highlight text={sec.title} q={q} />
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ borderTop: `1px solid ${tokens.line}` }}>
              {sec.intro && (
                <Typography variant="body2" sx={{ color: tokens.ink3, mb: 1.5 }}>
                  <Highlight text={sec.intro} q={q} />
                </Typography>
              )}
              <FeatureList items={sec.items} q={q} />
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" sx={{ color: tokens.ink4 }}>
        Tip: changes are live as soon as you Save. If something looks wrong on the website, re-open the matching screen here and check the Published / Visible switch. Still stuck? Contact your developer.
      </Typography>
    </Box>
  );
};
